import {
  BookingDto,
  IProduceButtonDto,
  IProduceJoinPassengerPreviewDto,
} from "../../dto";
import { DirectionEnum, EtlBookingTypeEnum, TimeZoneEnum } from "../../enums";
import { IProductionForm, ITypeormWhereCriteria } from "../../models";
import {
  addDaysPreservingWallClock,
  getDayjsFromTimeString,
} from "../date.helpers";
import { resolveProduceButtonDirection } from "../produce-button.helpers";

interface IProduceCriteriaParams {
  direction: DirectionEnum;
  shiftIds?: number[];
  unitIds?: number[];
  siteIds?: number[];
  passengerTypeId?: number[];
  areaIds?: number[];
  rideTypeId?: number;
  bookingType?: EtlBookingTypeEnum;
  sectors?: string[];
}

const buildProductionCriteria = (
  bookTimeFrom: TDateISO | undefined,
  bookTimeUntil: TDateISO | undefined,
  params: IProduceCriteriaParams,
): ITypeormWhereCriteria<BookingDto> & IProduceJoinPassengerPreviewDto => {
  const criteria: ITypeormWhereCriteria<BookingDto> &
    IProduceJoinPassengerPreviewDto = {
    direction: resolveProduceButtonDirection(params.direction),
  };

  if (bookTimeFrom && bookTimeUntil) {
    criteria.bookTime = {
      $between: [bookTimeFrom, bookTimeUntil],
    };
  }

  if (params.shiftIds?.length) {
    criteria.shiftId = { $in: params.shiftIds };
  }

  if (params.unitIds?.length) {
    criteria.passenger = {
      ...(criteria.passenger ?? {}),
      unitId: { $in: params.unitIds },
    };
  }

  if (params.siteIds?.length) {
    criteria.passenger = {
      ...(criteria.passenger ?? {}),
      siteId: { $in: params.siteIds },
    };
  }

  if (params.passengerTypeId) {
    criteria.passenger = {
      ...(criteria.passenger ?? {}),
      passengerTypeId: { $eq: params.passengerTypeId },
    };
  }

  if (params.areaIds?.length) {
    criteria.stationArea = { $any: params.areaIds };
  }

  if (params.bookingType) {
    criteria.bookingType = { $eq: params.bookingType };
  }

  if (params.sectors?.length) {
    criteria.passengerSector = { $in: params.sectors };
  }

  return criteria;
};

export const buildProductionCriteriaCustomProduceButton = (
  customProductionFormData: IProductionForm,
): ITypeormWhereCriteria<BookingDto> & IProduceJoinPassengerPreviewDto => {
  const bookTimeFrom = customProductionFormData.produceFrom;
  const bookTimeUntil = customProductionFormData.produceTo;

  const params: IProduceCriteriaParams = {
    direction: customProductionFormData.direction,
    shiftIds: customProductionFormData.shiftIds,
    unitIds: customProductionFormData.unitIds,
    siteIds: customProductionFormData.siteIds,
    passengerTypeId: customProductionFormData.passengerTypeId,
    areaIds: customProductionFormData.areaIds,
    bookingType: customProductionFormData.bookingType,
    rideTypeId: customProductionFormData.rideTypeId,
    sectors: customProductionFormData.sectors,
  };

  return buildProductionCriteria(bookTimeFrom, bookTimeUntil, params);
};

const bookTimeFromLocalTimeAndDayOffset = ({
  time,
  dayOffset,
  timezone,
}: {
  time: string | undefined;
  dayOffset: number | undefined;
  timezone: TimeZoneEnum;
}): TDateISO | undefined => {
  const todayAtTime = getDayjsFromTimeString(String(time), timezone);
  const instant = addDaysPreservingWallClock({
    date: todayAtTime.toDate(),
    days: dayOffset ?? 0,
    timezone,
  });

  return instant?.toISOString();
};

export const buildProductionCriteriaCellButton = (
  produceButtonDto: IProduceButtonDto,
  timezone: TimeZoneEnum,
): ITypeormWhereCriteria<BookingDto> & IProduceJoinPassengerPreviewDto => {
  const bookTimeFrom = bookTimeFromLocalTimeAndDayOffset({
    time: produceButtonDto.fromTime,
    dayOffset: produceButtonDto.fromDay,
    timezone,
  });

  const bookTimeUntil = bookTimeFromLocalTimeAndDayOffset({
    time: produceButtonDto.toTime,
    dayOffset: produceButtonDto.toDay,
    timezone,
  });

  const params: IProduceCriteriaParams = {
    direction: produceButtonDto.direction as DirectionEnum,
    shiftIds: produceButtonDto.shiftIds,
    unitIds: produceButtonDto.unitIds,
    siteIds: produceButtonDto.siteIds,
    areaIds: produceButtonDto.areaIds,
    bookingType: produceButtonDto?.metadata?.bookingType,
    sectors: produceButtonDto?.metadata?.sectors,
  };

  return buildProductionCriteria(bookTimeFrom, bookTimeUntil, params);
};
